import { parseISO, differenceInDays, format, eachMonthOfInterval } from "date-fns";
import { useLang } from "@/i18n";

const PHASE_COLORS = {
  development: "#1E3A8A",
  drilling: "#B45309",
  hauling: "#0F766E",
  ground_support: "#7C3AED",
  services: "#0369A1",
  ventilation: "#4D7C0F",
  other: "#475569",
};

const CUSTOM_PALETTE = ["#0EA5E9", "#DC2626", "#059669", "#9333EA", "#EA580C", "#0891B2", "#DB2777", "#65A30D", "#CA8A04", "#1F2937"];

function phaseColor(phase) {
  if (PHASE_COLORS[phase]) return PHASE_COLORS[phase];
  // Stable hash-based color for custom phases
  let hash = 0;
  for (let i = 0; i < phase.length; i += 1) {
    hash = ((hash << 5) - hash + phase.charCodeAt(i)) | 0;
  }
  return CUSTOM_PALETTE[Math.abs(hash) % CUSTOM_PALETTE.length];
}

const ROW_H = 44;

// A dep in a task can be either an old-format string (task_id) or new-format
// { p: task_id, t: 'FS'|'SS'|'FF'|'SF', l: lag_days }. Normalize to object shape.
function normalizeDep(d) {
  if (typeof d === "string") return { p: d, t: "FS", l: 0 };
  return { p: d.p, t: d.t || "FS", l: Number(d.l) || 0 };
}

function computeCriticalPath(tasks) {
  const byId = Object.fromEntries(tasks.map((x) => [x.id, x]));
  const dur = (x) => x.milestone ? 0 : Math.max(differenceInDays(parseISO(x.end_date), parseISO(x.start_date)), 1);
  const memo = {};
  const longest = (taskId) => {
    if (memo[taskId]) return memo[taskId];
    const deps = (byId[taskId].dependencies || [])
      .map(normalizeDep)
      .filter((d) => byId[d.p]);
    let best = { len: 0, path: [] };
    for (const d of deps) {
      const r = longest(d.p);
      // Lag adds to critical length; SS/FF/SF are approximations that still bubble the path
      const extra = d.t === "FS" ? 0 : 0;
      const candLen = r.len + extra + d.l;
      if (candLen > best.len) best = { len: candLen, path: r.path };
    }
    memo[taskId] = { len: best.len + dur(byId[taskId]), path: [...best.path, taskId] };
    return memo[taskId];
  };
  let best = { len: 0, path: [] };
  for (const x of tasks) {
    const r = longest(x.id);
    if (r.len > best.len) best = r;
  }
  return new Set(best.path);
}

const CANONICAL_ORDER = ["development", "drilling", "hauling", "ground_support", "services", "ventilation", "other"];

function buildRows(tasks) {
  const groups = new Map();
  tasks.forEach((task) => {
    if (!groups.has(task.phase)) groups.set(task.phase, []);
    groups.get(task.phase).push(task);
  });
  groups.forEach((arr) => arr.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")));
  const order = [];
  CANONICAL_ORDER.forEach((p) => { if (groups.has(p)) order.push(p); });
  tasks.forEach((task) => {
    if (!PHASE_COLORS[task.phase] && !order.includes(task.phase)) order.push(task.phase);
  });
  const rows = [];
  order.forEach((phase) => {
    rows.push({ type: "header", phase, color: phaseColor(phase), count: groups.get(phase).length });
    groups.get(phase).forEach((task) => rows.push({ type: "task", task, phase }));
  });
  return rows;
}

export default function GanttChart({ tasks, onTaskClick, baselineByTaskId = {} }) {
  const { t } = useLang();
  if (!tasks.length) return null;

  // Include baseline bounds in the timeline range so early-planned bars stay visible
  const baselineDates = Object.values(baselineByTaskId).flatMap((b) => (
    b?.baseline_start && b?.baseline_end ? [parseISO(b.baseline_start), parseISO(b.baseline_end)] : []
  ));
  const dates = tasks.flatMap((x) => [parseISO(x.start_date), parseISO(x.end_date)]).concat(baselineDates);
  const min = new Date(Math.min(...dates));
  const max = new Date(Math.max(...dates));
  const totalDays = Math.max(differenceInDays(max, min), 1);
  const pos = (d) => (differenceInDays(parseISO(d), min) / totalDays) * 100;
  const width = (s, e) => Math.max((differenceInDays(parseISO(e), parseISO(s)) / totalDays) * 100, 1);
  const months = eachMonthOfInterval({ start: min, end: max });
  const today = new Date();
  const todayPos = today >= min && today <= max ? (differenceInDays(today, min) / totalDays) * 100 : null;
  const crit = computeCriticalPath(tasks);
  const hasBaseline = Object.keys(baselineByTaskId).length > 0;

  const rows = buildRows(tasks);
  const rowIdx = {};
  rows.forEach((r, i) => { if (r.type === "task") rowIdx[r.task.id] = i; });
  const byId = Object.fromEntries(tasks.map((x) => [x.id, x]));
  const arrows = [];
  tasks.forEach((task) => {
    (task.dependencies || []).forEach((raw) => {
      const d = normalizeDep(raw);
      const pred = byId[d.p];
      if (!pred) return;
      // Arrow anchor depends on dep type:
      //   FS: pred.end   → task.start
      //   SS: pred.start → task.start
      //   FF: pred.end   → task.end
      //   SF: pred.start → task.end
      const predAnchor = d.t === "SS" || d.t === "SF" ? pred.start_date : pred.end_date;
      const taskAnchor = d.t === "FF" || d.t === "SF" ? task.end_date : task.start_date;
      arrows.push({
        id: `${d.p}->${task.id}-${d.t}`,
        type: d.t,
        lag: d.l,
        x1: pos(predAnchor), y1: rowIdx[d.p] * ROW_H + ROW_H / 2,
        x2: pos(taskAnchor), y2: rowIdx[task.id] * ROW_H + ROW_H / 2,
        crit: crit.has(task.id) && crit.has(d.p),
      });
    });
  });

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="gantt-chart">
      <div className="min-w-[800px]">
        <div className="flex border-b border-slate-200">
          <div className="w-64 shrink-0 px-4 py-2 overline-label border-r border-slate-200">{t("tasks")}</div>
          <div className="flex-1 relative h-8">
            {months.map((m) => (
              <span
                key={m.toISOString()}
                className="absolute top-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold"
                style={{ left: `${(differenceInDays(m, min) / totalDays) * 100}%` }}
              >
                {format(m, "MMM yyyy")}
              </span>
            ))}
          </div>
        </div>
        <div className="flex">
          <div className="w-64 shrink-0 border-r border-slate-200">
            {rows.map((row, i) => {
              if (row.type === "header") {
                const label = PHASE_COLORS[row.phase] ? t(row.phase) : row.phase;
                return (
                  <div
                    key={`h-${row.phase}`}
                    data-testid={`gantt-phase-header-${row.phase}`}
                    className="px-3 flex items-center gap-2 border-b border-slate-200 bg-slate-50"
                    style={{ height: ROW_H }}
                  >
                    <span className="h-3 w-1 rounded-sm shrink-0" style={{ backgroundColor: row.color }} />
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-700 truncate" title={label}>{label}</p>
                    <span className="text-[10px] text-slate-400 font-normal ml-auto">{row.count}</span>
                  </div>
                );
              }
              const task = row.task;
              return (
                <button
                  type="button"
                  key={`t-${task.id}`}
                  onClick={() => onTaskClick && onTaskClick(task)}
                  className={`w-full text-left pl-6 pr-3 flex flex-col justify-center border-b border-slate-100 transition-colors ${
                    onTaskClick ? "hover:bg-blue-50 cursor-pointer" : ""
                  }`}
                  style={{ height: ROW_H }}
                  data-testid={`gantt-task-row-${task.id}`}
                >
                  <p className="text-xs font-semibold text-slate-800 truncate" title={task.name}>{task.name}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider truncate">{task.assignee || "—"}</p>
                </button>
              );
            })}
          </div>
          <div className="flex-1 relative" style={{ height: rows.length * ROW_H }}>
            {rows.map((row, i) => (
              <div
                key={`bg-${i}`}
                className={`absolute inset-x-0 border-b ${row.type === "header" ? "border-slate-200 bg-slate-50" : "border-slate-100"}`}
                style={{ top: i * ROW_H, height: ROW_H }}
              />
            ))}
            {todayPos !== null && <div className="absolute inset-y-0 w-px bg-red-500/60 z-10" style={{ left: `${todayPos}%` }} />}
            {/* Baseline bars — thin grey outlines behind the actual bars */}
            {hasBaseline && rows.map((row, i) => {
              if (row.type !== "task") return null;
              const b = baselineByTaskId[row.task.id];
              if (!b?.baseline_start || !b?.baseline_end) return null;
              return (
                <div
                  key={`base-${row.task.id}`}
                  className="absolute h-2 rounded-sm border border-slate-400/70 bg-slate-200/70 z-[1]"
                  style={{
                    top: i * ROW_H + (ROW_H - 4),
                    left: `${pos(b.baseline_start)}%`,
                    width: `${width(b.baseline_start, b.baseline_end)}%`,
                  }}
                  data-testid={`gantt-baseline-bar-${row.task.id}`}
                  title={`Línea base: ${b.baseline_start} → ${b.baseline_end}`}
                />
              );
            })}
            {rows.map((row, i) => {
              if (row.type !== "task") return null;
              const task = row.task;
              // Milestone → rombo (diamond)
              if (task.milestone) {
                const size = 16;
                return (
                  <button
                    type="button"
                    key={`ms-${task.id}`}
                    onClick={() => onTaskClick && onTaskClick(task)}
                    className="absolute z-[2] cursor-pointer"
                    style={{
                      top: i * ROW_H + (ROW_H - size) / 2,
                      left: `calc(${pos(task.start_date)}% - ${size / 2}px)`,
                      width: size, height: size,
                    }}
                    title={`Hito · ${task.name} · ${task.start_date}`}
                    data-testid={`gantt-milestone-${task.id}`}
                  >
                    <svg viewBox="0 0 16 16" width={size} height={size}>
                      <polygon
                        points="8,0 16,8 8,16 0,8"
                        fill={crit.has(task.id) ? "#EF4444" : "#1E293B"}
                        stroke="#0F172A"
                        strokeWidth="1"
                      />
                    </svg>
                  </button>
                );
              }
              return (
                <div
                  key={`bar-${task.id}`}
                  onClick={() => onTaskClick && onTaskClick(task)}
                  className={`absolute h-5 rounded-sm overflow-hidden z-[1] ${onTaskClick ? "cursor-pointer hover:ring-2 hover:ring-blue-400/70" : ""}`}
                  style={{
                    top: i * ROW_H + (ROW_H - 20) / 2,
                    left: `${pos(task.start_date)}%`,
                    width: `${width(task.start_date, task.end_date)}%`,
                    backgroundColor: `${phaseColor(task.phase)}30`,
                    boxShadow: crit.has(task.id) ? "0 0 0 2px #EF4444" : undefined,
                  }}
                  title={`${task.name} — ${task.progress}%`}
                  data-testid={`gantt-task-bar-${task.id}`}
                >
                  <div className="h-full" style={{ width: `${task.progress}%`, backgroundColor: phaseColor(task.phase) }} />
                </div>
              );
            })}
            <svg className="absolute inset-0 w-full pointer-events-none" style={{ height: rows.length * ROW_H }} data-testid="gantt-arrows">
              <defs>
                <marker id="gantt-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 z" fill="#64748B" />
                </marker>
              </defs>
              {arrows.map((a) => (
                <g key={a.id}>
                  <line x1={`${a.x1}%`} y1={a.y1} x2={`${a.x2}%`} y2={a.y2}
                    stroke={a.crit ? "#EF4444" : "#64748B"} strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#gantt-arrow)" />
                  <text x={`calc(${(a.x1 + a.x2) / 2}%)`} y={(a.y1 + a.y2) / 2 - 3}
                    fontSize="9" fontWeight="700"
                    fill={a.crit ? "#B91C1C" : "#475569"}
                    style={{ pointerEvents: "none" }}
                    data-testid={`gantt-arrow-label-${a.id}`}>
                    {a.type}{a.lag ? (a.lag > 0 ? `+${a.lag}d` : `${a.lag}d`) : ""}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </div>
        <div className="flex items-center gap-4 px-4 py-2 flex-wrap border-t border-slate-200">
          {(() => {
            const legend = [];
            const seen = new Set();
            tasks.forEach((task) => {
              if (!seen.has(task.phase)) {
                seen.add(task.phase);
                legend.push({ phase: task.phase, color: phaseColor(task.phase) });
              }
            });
            return legend.map(({ phase, color }) => (
              <span key={phase} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: color }} /> {PHASE_COLORS[phase] ? t(phase) : phase}
              </span>
            ));
          })()}
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            <span className="h-3 w-px bg-red-500" /> {t("today")}
          </span>
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-red-500" data-testid="gantt-critical-legend">
            <span className="h-2 w-4 rounded-sm border-2 border-red-500" /> {t("criticalPath")}
          </span>
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            <svg width="10" height="10" viewBox="0 0 16 16"><polygon points="8,0 16,8 8,16 0,8" fill="#1E293B" /></svg> Hito
          </span>
          {hasBaseline && (
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500" data-testid="gantt-baseline-legend">
              <span className="h-1 w-4 rounded-sm border border-slate-400 bg-slate-200" /> Línea base
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
